// Ported from static/index.html v1. Turns a `cycle_steps` row into a
// non-technical, single-sentence description for the StepTrace drawer.

import type { BackendCycleStep } from "@/store/api";

function tryParseJson(s: string | null | undefined): unknown {
  if (!s) return null;
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function asObject(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

export function plainEnglish(step: BackendCycleStep): string {
  const { name, status } = step;
  const summary = step.summary ?? "";
  const detailRaw = tryParseJson(step.detail_json ?? null);
  const detail = asObject(detailRaw) ?? {};

  switch (name) {
    case "gate":
      return status === "skip"
        ? `The bot is currently stopped, so this cycle was skipped without doing anything. ${summary}`
        : `Cycle gate — ${summary}`;

    case "snapshot": {
      const balances = asObject(detail.balances) ?? {};
      const free = (detail.free_quote_for_buying as number | undefined) ?? 0;
      const positions = (detail.total_position_value_usd as number | undefined) ?? 0;
      const symCount = Array.isArray(detail.markets) ? detail.markets.length : 0;
      const assets = Object.keys(balances).slice(0, 6).join(", ");
      return (
        `Looked at your Binance account: about $${free.toFixed(2)} spendable cash and ` +
        `$${positions.toFixed(2)} worth of crypto already held. ` +
        (assets ? `Wallet contains ${assets}. ` : "") +
        `Pulled fresh prices + indicators for ${symCount} pairs.`
      );
    }

    case "market_analysis": {
      const text = (detail.text as string | undefined) ?? summary ?? "";
      const lines = text.trim().split("\n").filter((l) => l.trim()).length;
      return (
        `The Market Analyst agent read each pair's price, RSI, and moving averages and wrote ` +
        `${lines} line${lines === 1 ? "" : "s"} of notes about which markets look interesting ` +
        `(open the JSON below for the full take).`
      );
    }

    case "draft_trade": {
      const obj = asObject(tryParseJson(detail.text as string | undefined));
      if (!obj) return "The Trade Proposer agent drafted a trade. (See JSON below.)";
      if (obj.action === "HOLD") {
        return (
          `The Trade Proposer agent decided not to trade this cycle (HOLD). ` +
          (obj.reasoning ? `Reason: ${obj.reasoning}` : "")
        );
      }
      const conf = obj.confidence as number | undefined;
      return (
        `The Trade Proposer agent suggested ${obj.action} ${obj.symbol} for about $${obj.size_usd}, ` +
        `with confidence ${conf != null ? (conf * 100).toFixed(0) : "?"}%. ` +
        (obj.reasoning ? `Reasoning: ${obj.reasoning}` : "")
      );
    }

    case "final_decision": {
      const obj = asObject(tryParseJson(detail.text as string | undefined));
      if (!obj) return "The Risk Reviewer agent gave its final verdict. (See JSON below.)";
      if (obj.action === "HOLD") {
        return (
          `The Risk Reviewer agent overruled the draft and chose HOLD (do nothing). ` +
          (obj.reasoning ? `Reason: ${obj.reasoning}` : "")
        );
      }
      const conf = obj.confidence as number | undefined;
      return (
        `The Risk Reviewer agent approved the proposal: ${obj.action} ${obj.symbol} for ~$${obj.size_usd} ` +
        `(confidence ${conf != null ? (conf * 100).toFixed(0) : "?"}%). ` +
        (obj.reasoning ? `Reasoning: ${obj.reasoning}` : "")
      );
    }

    case "risk_guard":
      if (status === "ok") {
        return (
          `The hard-coded safety rails (per-trade cap, total exposure cap, daily limit, cooldown) ` +
          `all passed — ${summary}.`
        );
      }
      return (
        `The hard-coded safety rails REJECTED the trade. Reason: ${summary}. ` +
        `Nothing was sent to Binance and no money moved.`
      );

    case "outcome":
      return "Cycle finished without making a trade (HOLD). The bot will check again on the next schedule.";

    case "queue":
      return (
        "The trade was put into the Pending Approvals queue and is waiting for you to click Approve. " +
        "Until you do, no order is sent. Open the Pending tab to act on it."
      );

    case "execute":
      if (summary.includes("dry_run=True")) {
        return (
          "Dry-run is ON, so the order was logged but NOT actually sent to Binance. " +
          "Switch off Dry-run in Settings to trade for real."
        );
      }
      return `The order was placed on Binance: ${summary}. Check the Trades tab for fill details.`;

    case "error":
      return (
        `Something went wrong this cycle: ${summary}. Nothing was traded. ` +
        `Most common causes: bad API key, network glitch, or Binance rate limit.`
      );

    default:
      return summary || `Step "${name}" finished with status ${status}.`;
  }
}
